// Firestore Service for Admin CRUD Operations

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot,
  writeBatch,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toDate } from '@/lib/utils/date';
import {
  Workspace,
  Property,
  Keyword,
  Prompt,
  LLM,
  WorkspaceFormData,
  PropertyFormData,
  KeywordFormData,
  PromptFormData,
  LLMFormData,
  EntityStatus,
  ClientUser,
  ClientUserAccess,
  ClientUserFormData,
  ClientUserStatus,
  AdminUser,
  AdminUserFormData,
  AdminUserStatus,
} from '@/lib/types/admin';

// Collection names
const COLLECTIONS = {
  workspaces: 'workspaces',
  properties: 'properties',
  keywords: 'keywords',
  prompts: 'prompts',
  llms: 'llms',
  clientUsers: 'clientUsers',
  clientUserAccess: 'clientUserAccess',
  adminUsers: 'adminUsers',
} as const;

// Helper to convert document to typed object
const docToEntity = <T>(doc: QueryDocumentSnapshot<DocumentData>): T => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    createdAt: data.createdAt ? toDate(data.createdAt) : new Date(),
    updatedAt: data.updatedAt ? toDate(data.updatedAt) : new Date(),
  } as T;
};

// ==================== WORKSPACES ====================

export async function getWorkspaces(): Promise<Workspace[]> {
  const q = query(
    collection(db, COLLECTIONS.workspaces),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => docToEntity<Workspace>(doc));
}

export interface PaginatedResult<T> {
  data: T[];
  totalCount: number;
  hasMore: boolean;
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  firstDoc: QueryDocumentSnapshot<DocumentData> | null;
}

// Get total count of workspaces (for pagination info) - uses server-side aggregation
export async function getWorkspacesCount(): Promise<number> {
  const coll = collection(db, COLLECTIONS.workspaces);
  const snapshot = await getCountFromServer(coll);
  return snapshot.data().count;
}

export async function getWorkspacesPaginated(
  pageSize: number = 10,
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null,
  searchTerm?: string
): Promise<PaginatedResult<Workspace>> {
  // If searching, we need to fetch all and filter (Firestore limitation)
  if (searchTerm) {
    const allQ = query(
      collection(db, COLLECTIONS.workspaces),
      orderBy('createdAt', 'desc')
    );
    const allSnapshot = await getDocs(allQ);
    const term = searchTerm.toLowerCase();
    const filteredDocs = allSnapshot.docs.filter(d => {
      const data = d.data();
      return data.name?.toLowerCase().includes(term);
    });
    
    // Find the starting index based on lastDoc
    let startIndex = 0;
    if (lastDoc) {
      const lastIndex = filteredDocs.findIndex(d => d.id === lastDoc.id);
      startIndex = lastIndex >= 0 ? lastIndex + 1 : 0;
    }
    
    const resultDocs = filteredDocs.slice(startIndex, startIndex + pageSize);
    const hasMore = startIndex + pageSize < filteredDocs.length;
    
    return {
      data: resultDocs.map((doc) => docToEntity<Workspace>(doc)),
      totalCount: filteredDocs.length,
      hasMore,
      lastDoc: resultDocs.length > 0 ? resultDocs[resultDocs.length - 1] : null,
      firstDoc: resultDocs.length > 0 ? resultDocs[0] : null,
    };
  }

  // Without search, use efficient cursor-based pagination
  let q = query(
    collection(db, COLLECTIONS.workspaces),
    orderBy('createdAt', 'desc'),
    limit(pageSize)
  );

  if (lastDoc) {
    q = query(
      collection(db, COLLECTIONS.workspaces),
      orderBy('createdAt', 'desc'),
      startAfter(lastDoc),
      limit(pageSize)
    );
  }

  const snapshot = await getDocs(q);
  const docs = snapshot.docs;
  
  // Get total count for UI
  const totalCount = await getWorkspacesCount();
  
  // Check if there are more by trying to fetch one more
  let hasMore = false;
  if (docs.length === pageSize) {
    const checkMoreQ = query(
      collection(db, COLLECTIONS.workspaces),
      orderBy('createdAt', 'desc'),
      startAfter(docs[docs.length - 1]),
      limit(1)
    );
    const checkMoreSnapshot = await getDocs(checkMoreQ);
    hasMore = !checkMoreSnapshot.empty;
  }
  
  return {
    data: docs.map((doc) => docToEntity<Workspace>(doc)),
    totalCount,
    hasMore,
    lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
    firstDoc: docs.length > 0 ? docs[0] : null,
  };
}

export async function getWorkspace(id: string): Promise<Workspace | null> {
  const docRef = doc(db, COLLECTIONS.workspaces, id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  const data = docSnap.data();
  return { 
    id: docSnap.id, 
    ...data,
    createdAt: data.createdAt ? toDate(data.createdAt) : new Date(),
    updatedAt: data.updatedAt ? toDate(data.updatedAt) : new Date(),
  } as Workspace;
}

export async function createWorkspace(
  data: WorkspaceFormData,
  createdBy: string
): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTIONS.workspaces), {
    ...data,
    createdBy,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
}

export async function updateWorkspace(
  id: string,
  data: Partial<WorkspaceFormData>
): Promise<void> {
  const docRef = doc(db, COLLECTIONS.workspaces, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

export async function deleteWorkspace(id: string): Promise<void> {
  const batch = writeBatch(db);
  
  // Get all properties under this workspace
  const properties = await getPropertiesByWorkspace(id);
  
  // For each property, get and delete keywords and prompts
  for (const property of properties) {
    const keywords = await getKeywordsByProperty(property.id);
    for (const keyword of keywords) {
      const prompts = await getPromptsByKeyword(keyword.id);
      for (const prompt of prompts) {
        batch.delete(doc(db, COLLECTIONS.prompts, prompt.id));
      }
      batch.delete(doc(db, COLLECTIONS.keywords, keyword.id));
    }
    batch.delete(doc(db, COLLECTIONS.properties, property.id));
  }
  
  // Delete the workspace
  batch.delete(doc(db, COLLECTIONS.workspaces, id));
  
  // Commit the batch
  await batch.commit();
}

// ==================== PROPERTIES ====================

export async function getPropertiesByWorkspace(
  workspaceId: string
): Promise<Property[]> {
  const q = query(
    collection(db, COLLECTIONS.properties),
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => docToEntity<Property>(doc));
}

// Get count of properties for a workspace - uses server-side aggregation
export async function getPropertiesCountByWorkspace(workspaceId: string): Promise<number> {
  const q = query(
    collection(db, COLLECTIONS.properties),
    where('workspaceId', '==', workspaceId)
  );
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
}

// Paginated properties fetch
export async function getPropertiesByWorkspacePaginated(
  workspaceId: string,
  pageSize: number = 10,
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null,
  searchTerm?: string,
  statusFilter?: EntityStatus | 'all'
): Promise<PaginatedResult<Property>> {
  // For search/filter, we need to fetch all matching and filter
  if (searchTerm || (statusFilter && statusFilter !== 'all')) {
    const allQ = query(
      collection(db, COLLECTIONS.properties),
      where('workspaceId', '==', workspaceId),
      orderBy('createdAt', 'desc')
    );
    const allSnapshot = await getDocs(allQ);
    
    let filteredDocs = allSnapshot.docs;
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredDocs = filteredDocs.filter(d => {
        const data = d.data();
        return data.name?.toLowerCase().includes(term) || 
               data.websiteUrl?.toLowerCase().includes(term);
      });
    }
    
    // Apply status filter
    if (statusFilter && statusFilter !== 'all') {
      filteredDocs = filteredDocs.filter(d => {
        const data = d.data();
        return data.status === statusFilter;
      });
    }
    
    // Find the starting index based on lastDoc
    let startIndex = 0;
    if (lastDoc) {
      const lastIndex = filteredDocs.findIndex(d => d.id === lastDoc.id);
      startIndex = lastIndex >= 0 ? lastIndex + 1 : 0;
    }
    
    const resultDocs = filteredDocs.slice(startIndex, startIndex + pageSize);
    const hasMore = startIndex + pageSize < filteredDocs.length;
    
    return {
      data: resultDocs.map((doc) => docToEntity<Property>(doc)),
      totalCount: filteredDocs.length,
      hasMore,
      lastDoc: resultDocs.length > 0 ? resultDocs[resultDocs.length - 1] : null,
      firstDoc: resultDocs.length > 0 ? resultDocs[0] : null,
    };
  }

  // Without search/filter, use efficient cursor-based pagination
  let q = query(
    collection(db, COLLECTIONS.properties),
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc'),
    limit(pageSize)
  );

  if (lastDoc) {
    q = query(
      collection(db, COLLECTIONS.properties),
      where('workspaceId', '==', workspaceId),
      orderBy('createdAt', 'desc'),
      startAfter(lastDoc),
      limit(pageSize)
    );
  }

  const snapshot = await getDocs(q);
  const docs = snapshot.docs;
  
  // Get total count for UI
  const totalCount = await getPropertiesCountByWorkspace(workspaceId);
  
  // Check if there are more by trying to fetch one more
  let hasMore = false;
  if (docs.length === pageSize) {
    const checkMoreQ = query(
      collection(db, COLLECTIONS.properties),
      where('workspaceId', '==', workspaceId),
      orderBy('createdAt', 'desc'),
      startAfter(docs[docs.length - 1]),
      limit(1)
    );
    const checkMoreSnapshot = await getDocs(checkMoreQ);
    hasMore = !checkMoreSnapshot.empty;
  }
  
  return {
    data: docs.map((doc) => docToEntity<Property>(doc)),
    totalCount,
    hasMore,
    lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
    firstDoc: docs.length > 0 ? docs[0] : null,
  };
}

export async function getProperty(id: string): Promise<Property | null> {
  const docRef = doc(db, COLLECTIONS.properties, id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  const data = docSnap.data();
  return { 
    id: docSnap.id, 
    ...data,
    createdAt: data.createdAt ? toDate(data.createdAt) : new Date(),
    updatedAt: data.updatedAt ? toDate(data.updatedAt) : new Date(),
  } as Property;
}

export async function createProperty(
  workspaceId: string,
  data: PropertyFormData
): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTIONS.properties), {
    ...data,
    workspaceId,
    status: 'active' as EntityStatus,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
}

export async function updateProperty(
  id: string,
  data: Partial<PropertyFormData & { status: EntityStatus }>
): Promise<void> {
  const docRef = doc(db, COLLECTIONS.properties, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

export async function deleteProperty(id: string): Promise<void> {
  const batch = writeBatch(db);
  
  // Get all keywords under this property
  const keywords = await getKeywordsByProperty(id);
  
  // For each keyword, delete its prompts
  for (const keyword of keywords) {
    const prompts = await getPromptsByKeyword(keyword.id);
    for (const prompt of prompts) {
      batch.delete(doc(db, COLLECTIONS.prompts, prompt.id));
    }
    batch.delete(doc(db, COLLECTIONS.keywords, keyword.id));
  }
  
  // Delete the property
  batch.delete(doc(db, COLLECTIONS.properties, id));
  
  // Commit the batch
  await batch.commit();
}

// ==================== KEYWORDS ====================

export async function getKeywordsByProperty(
  propertyId: string
): Promise<Keyword[]> {
  const q = query(
    collection(db, COLLECTIONS.keywords),
    where('propertyId', '==', propertyId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => docToEntity<Keyword>(doc));
}

export async function getKeyword(id: string): Promise<Keyword | null> {
  const docRef = doc(db, COLLECTIONS.keywords, id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  const data = docSnap.data();
  return { 
    id: docSnap.id, 
    ...data,
    createdAt: data.createdAt ? toDate(data.createdAt) : new Date(),
    updatedAt: data.updatedAt ? toDate(data.updatedAt) : new Date(),
  } as Keyword;
}

export async function createKeyword(
  propertyId: string,
  data: KeywordFormData
): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTIONS.keywords), {
    ...data,
    propertyId,
    status: 'active' as EntityStatus,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
}

export async function updateKeyword(
  id: string,
  data: Partial<KeywordFormData & { status: EntityStatus }>
): Promise<void> {
  const docRef = doc(db, COLLECTIONS.keywords, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

export async function deleteKeyword(id: string): Promise<void> {
  const batch = writeBatch(db);
  
  // Delete all prompts under this keyword
  const prompts = await getPromptsByKeyword(id);
  for (const prompt of prompts) {
    batch.delete(doc(db, COLLECTIONS.prompts, prompt.id));
  }
  
  // Delete the keyword
  batch.delete(doc(db, COLLECTIONS.keywords, id));
  
  // Commit the batch
  await batch.commit();
}

// ==================== PROMPTS ====================

export async function getPromptsByKeyword(keywordId: string): Promise<Prompt[]> {
  const q = query(
    collection(db, COLLECTIONS.prompts),
    where('keywordId', '==', keywordId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => docToEntity<Prompt>(doc));
}

export async function getPrompt(id: string): Promise<Prompt | null> {
  const docRef = doc(db, COLLECTIONS.prompts, id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  const data = docSnap.data();
  return { 
    id: docSnap.id, 
    ...data,
    createdAt: data.createdAt ? toDate(data.createdAt) : new Date(),
    updatedAt: data.updatedAt ? toDate(data.updatedAt) : new Date(),
  } as Prompt;
}

export async function createPrompt(
  keywordId: string,
  data: PromptFormData
): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTIONS.prompts), {
    ...data,
    keywordId,
    status: 'active' as EntityStatus,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
}

export async function updatePrompt(
  id: string,
  data: Partial<PromptFormData & { status: EntityStatus }>
): Promise<void> {
  const docRef = doc(db, COLLECTIONS.prompts, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

export async function deletePrompt(id: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.prompts, id);
  await deleteDoc(docRef);
}

// ==================== LLMs ====================

export async function getLLMs(): Promise<LLM[]> {
  const q = query(
    collection(db, COLLECTIONS.llms),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => docToEntity<LLM>(doc));
}

export async function getActiveLLMs(): Promise<LLM[]> {
  // Get all LLMs and filter in memory to avoid needing composite index
  const allLLMs = await getLLMs();
  return allLLMs.filter(llm => llm.status === 'active');
}

export async function getLLM(id: string): Promise<LLM | null> {
  const docRef = doc(db, COLLECTIONS.llms, id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  const data = docSnap.data();
  return { 
    id: docSnap.id, 
    ...data,
    createdAt: data.createdAt ? toDate(data.createdAt) : new Date(),
    updatedAt: data.updatedAt ? toDate(data.updatedAt) : new Date(),
  } as LLM;
}

export async function createLLM(data: LLMFormData): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTIONS.llms), {
    ...data,
    status: 'active',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
}

export async function updateLLM(
  id: string,
  data: Partial<LLMFormData & { status: 'active' | 'inactive' }>
): Promise<void> {
  const docRef = doc(db, COLLECTIONS.llms, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

export async function deleteLLM(id: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.llms, id);
  await deleteDoc(docRef);
}

/**
 * Get LLMs by their IDs
 * Used to fetch configured LLMs for a property with their logos
 */
export async function getLLMsByIds(llmIds: string[]): Promise<LLM[]> {
  if (!llmIds || llmIds.length === 0) {
    return [];
  }
  
  const llms: LLM[] = [];
  for (const id of llmIds) {
    const llm = await getLLM(id);
    if (llm) {
      llms.push(llm);
    }
  }
  return llms;
}

// ==================== BULK OPERATIONS ====================

export async function createKeywordsWithPrompts(
  propertyId: string,
  keywords: { text: string; prompts: { text: string; source: 'auto' | 'manual' }[] }[]
): Promise<void> {
  for (const kw of keywords) {
    const keywordId = await createKeyword(propertyId, { text: kw.text });
    for (const prompt of kw.prompts) {
      await createPrompt(keywordId, prompt);
    }
  }
}

// ==================== SEED DATA ====================

export async function seedDefaultLLMs(): Promise<void> {
  const existingLLMs = await getLLMs();
  
  const defaultLLMs = [
    {
      name: 'ChatGPT',
      modelName: 'gpt-4o',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg',
    },
    {
      name: 'Gemini',
      modelName: 'gemini-1.5-flash',
      logoUrl: 'https://www.gstatic.com/lamda/images/gemini_favicon_f069958c85030f21e9eafa.png',
    },
  ];

  for (const llm of defaultLLMs) {
    const exists = existingLLMs.some(
      (e) => e.name.toLowerCase() === llm.name.toLowerCase()
    );
    if (!exists) {
      await createLLM(llm);
    }
  }
}

export async function seedExampleData(userEmail: string): Promise<void> {
  // Check if example workspace already exists
  const workspaces = await getWorkspaces();
  if (workspaces.some(w => w.name === 'Hilton Sri Lanka')) {
    return; // Already seeded
  }

  // Create example workspace
  const workspaceId = await createWorkspace(
    { name: 'Hilton Sri Lanka' },
    userEmail
  );

  // Get active LLMs
  const llms = await getActiveLLMs();
  const llmIds = llms.map(l => l.id);

  // Create example property
  const propertyId = await createProperty(workspaceId, {
    name: 'Hilton Colombo',
    websiteUrl: 'https://www.hilton.com/en/hotels/colombo/',
    businessType: 'hotel',
    scanFrequency: 'weekly',
    competitors: ['Shangri-La Colombo', 'Cinnamon Grand', 'Taj Samudra'],
    llmIds,
  });

  // Create example keywords with prompts
  await createKeywordsWithPrompts(propertyId, [
    {
      text: 'best luxury hotels in Colombo',
      prompts: [
        { text: "I'm planning a business trip to Colombo, Sri Lanka. What are the best luxury hotels there?", source: 'auto' },
        { text: "Where should I stay in Colombo for a 5-star experience with great amenities?", source: 'auto' },
      ],
    },
    {
      text: 'hotels near colombo fort',
      prompts: [
        { text: "I need a hotel near Colombo Fort for easy access to the business district. Any recommendations?", source: 'auto' },
        { text: "What are the top-rated hotels close to Colombo Fort railway station?", source: 'auto' },
      ],
    },
  ]);
}

// ==================== CLIENT USERS ====================

export async function getClientUsers(): Promise<ClientUser[]> {
  const q = query(
    collection(db, COLLECTIONS.clientUsers),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => docToEntity<ClientUser>(doc));
}

export async function getClientUser(id: string): Promise<ClientUser | null> {
  const docRef = doc(db, COLLECTIONS.clientUsers, id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  const data = docSnap.data();
  return { 
    id: docSnap.id, 
    ...data,
    createdAt: data.createdAt ? toDate(data.createdAt) : new Date(),
    updatedAt: data.updatedAt ? toDate(data.updatedAt) : new Date(),
  } as ClientUser;
}

export async function getClientUserByEmail(email: string): Promise<ClientUser | null> {
  const q = query(
    collection(db, COLLECTIONS.clientUsers),
    where('email', '==', email.toLowerCase()),
    limit(1)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return docToEntity<ClientUser>(snapshot.docs[0]);
}

export async function createClientUser(
  data: ClientUserFormData,
  createdBy: string,
  firebaseUid?: string
): Promise<string> {
  // If firebaseUid provided, use it as doc ID (keeps IDs in sync)
  if (firebaseUid) {
    const docRef = doc(db, COLLECTIONS.clientUsers, firebaseUid);
    await setDoc(docRef, {
      email: data.email.toLowerCase(),
      name: data.name,
      status: 'pending' as ClientUserStatus,
      requiresPasswordReset: true,
      createdBy,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return firebaseUid;
  }
  
  // Fallback to auto-generated ID
  const docRef = await addDoc(collection(db, COLLECTIONS.clientUsers), {
    email: data.email.toLowerCase(),
    name: data.name,
    status: 'pending' as ClientUserStatus,
    requiresPasswordReset: true,
    createdBy,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
}

export async function updateClientUser(
  id: string,
  data: Partial<ClientUserFormData & { status: ClientUserStatus; requiresPasswordReset: boolean; lastLoginAt: Date }>
): Promise<void> {
  const docRef = doc(db, COLLECTIONS.clientUsers, id);
  const updateData: Record<string, unknown> = {
    ...data,
    updatedAt: Timestamp.now(),
  };
  if (data.lastLoginAt) {
    updateData.lastLoginAt = Timestamp.fromDate(data.lastLoginAt);
  }
  await updateDoc(docRef, updateData);
}

export async function deleteClientUser(id: string): Promise<void> {
  const batch = writeBatch(db);
  
  // Delete all access records for this user
  const accessRecords = await getClientUserAccess(id);
  for (const access of accessRecords) {
    batch.delete(doc(db, COLLECTIONS.clientUserAccess, access.id));
  }
  
  // Delete the user
  batch.delete(doc(db, COLLECTIONS.clientUsers, id));
  
  // Commit the batch
  await batch.commit();
}

// ==================== CLIENT USER ACCESS ====================

export async function getClientUserAccess(clientUserId: string): Promise<ClientUserAccess[]> {
  const q = query(
    collection(db, COLLECTIONS.clientUserAccess),
    where('clientUserId', '==', clientUserId),
    orderBy('grantedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => docToEntity<ClientUserAccess>(doc));
}

export async function grantAccess(
  clientUserId: string,
  access: { workspaceId?: string; propertyId?: string },
  grantedBy: string
): Promise<string> {
  // Check if access already exists
  const existingAccess = await getClientUserAccess(clientUserId);
  const alreadyExists = existingAccess.some(
    (a) => 
      (access.workspaceId && a.workspaceId === access.workspaceId && !a.propertyId) ||
      (access.propertyId && a.propertyId === access.propertyId)
  );
  
  if (alreadyExists) {
    throw new Error('Access already granted');
  }

  const docRef = await addDoc(collection(db, COLLECTIONS.clientUserAccess), {
    clientUserId,
    workspaceId: access.workspaceId || null,
    propertyId: access.propertyId || null,
    accessLevel: 'view',
    grantedBy,
    grantedAt: Timestamp.now(),
  });
  return docRef.id;
}

export async function revokeAccess(accessId: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.clientUserAccess, accessId);
  await deleteDoc(docRef);
}

// Get all workspaces accessible to a client user
export async function getAccessibleWorkspaces(clientUserId: string): Promise<Workspace[]> {
  const accessRecords = await getClientUserAccess(clientUserId);
  
  // Get workspace IDs from direct workspace access
  const workspaceIds = new Set<string>();
  
  for (const access of accessRecords) {
    if (access.workspaceId && !access.propertyId) {
      // Direct workspace access
      workspaceIds.add(access.workspaceId);
    } else if (access.propertyId) {
      // Property-level access - need to get the workspace
      const property = await getProperty(access.propertyId);
      if (property) {
        workspaceIds.add(property.workspaceId);
      }
    }
  }
  
  // Fetch all accessible workspaces
  const workspaces: Workspace[] = [];
  for (const id of workspaceIds) {
    const workspace = await getWorkspace(id);
    if (workspace) {
      workspaces.push(workspace);
    }
  }
  
  return workspaces;
}

// Get all properties accessible to a client user (within a specific workspace)
export async function getAccessibleProperties(
  clientUserId: string,
  workspaceId?: string
): Promise<Property[]> {
  const accessRecords = await getClientUserAccess(clientUserId);
  
  const accessibleProperties: Property[] = [];
  const addedPropertyIds = new Set<string>();
  
  for (const access of accessRecords) {
    // Check for full workspace access
    if (access.workspaceId && !access.propertyId) {
      if (!workspaceId || access.workspaceId === workspaceId) {
        // User has access to all properties in this workspace
        const properties = await getPropertiesByWorkspace(access.workspaceId);
        for (const prop of properties) {
          if (!addedPropertyIds.has(prop.id)) {
            accessibleProperties.push(prop);
            addedPropertyIds.add(prop.id);
          }
        }
      }
    } else if (access.propertyId) {
      // Property-level access
      const property = await getProperty(access.propertyId);
      if (property && !addedPropertyIds.has(property.id)) {
        if (!workspaceId || property.workspaceId === workspaceId) {
          accessibleProperties.push(property);
          addedPropertyIds.add(property.id);
        }
      }
    }
  }
  
  return accessibleProperties;
}

// Check if a client user has access to a specific property
export async function hasAccessToProperty(
  clientUserId: string,
  propertyId: string
): Promise<boolean> {
  const property = await getProperty(propertyId);
  if (!property) return false;
  
  const accessRecords = await getClientUserAccess(clientUserId);
  
  for (const access of accessRecords) {
    // Check for workspace-level access
    if (access.workspaceId === property.workspaceId && !access.propertyId) {
      return true;
    }
    // Check for property-level access
    if (access.propertyId === propertyId) {
      return true;
    }
  }
  
  return false;
}

// ==================== ADMIN USERS ====================

export async function getAdminUsers(): Promise<AdminUser[]> {
  const q = query(
    collection(db, COLLECTIONS.adminUsers),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      email: data.email || docSnap.id,
      name: data.name || '',
      status: data.status || 'active',
      createdAt: data.createdAt ? toDate(data.createdAt) : new Date(),
      updatedAt: data.updatedAt ? toDate(data.updatedAt) : new Date(),
      createdBy: data.createdBy,
    } as AdminUser;
  });
}

export async function getAdminUser(email: string): Promise<AdminUser | null> {
  const docRef = doc(db, COLLECTIONS.adminUsers, email.toLowerCase());
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  const data = docSnap.data();
  return {
    id: docSnap.id,
    email: data.email || docSnap.id,
    name: data.name || '',
    status: data.status || 'active',
    createdAt: data.createdAt ? toDate(data.createdAt) : new Date(),
    updatedAt: data.updatedAt ? toDate(data.updatedAt) : new Date(),
    createdBy: data.createdBy,
  } as AdminUser;
}

export async function createAdminUser(
  data: AdminUserFormData,
  createdBy: string
): Promise<string> {
  const email = data.email.toLowerCase();
  
  // Check if admin user already exists
  const existing = await getAdminUser(email);
  if (existing) {
    throw new Error('Admin user with this email already exists');
  }
  
  // Use email as document ID
  const docRef = doc(db, COLLECTIONS.adminUsers, email);
  await setDoc(docRef, {
    email: email,
    name: data.name,
    status: 'active' as AdminUserStatus,
    createdBy,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return email;
}

export async function updateAdminUser(
  email: string,
  data: Partial<AdminUserFormData & { status: AdminUserStatus }>
): Promise<void> {
  const docRef = doc(db, COLLECTIONS.adminUsers, email.toLowerCase());
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

export async function deleteAdminUser(email: string): Promise<void> {
  const docRef = doc(db, COLLECTIONS.adminUsers, email.toLowerCase());
  await deleteDoc(docRef);
}

// Get counts for dashboard - uses server-side aggregation for efficiency
export async function getDashboardStats(): Promise<{
  workspacesCount: number;
  propertiesCount: number;
  clientUsersCount: number;
  adminUsersCount: number;
}> {
  const [workspacesSnapshot, propertiesSnapshot, clientUsersSnapshot, adminUsersSnapshot] = await Promise.all([
    getCountFromServer(collection(db, COLLECTIONS.workspaces)),
    getCountFromServer(collection(db, COLLECTIONS.properties)),
    getCountFromServer(collection(db, COLLECTIONS.clientUsers)),
    getCountFromServer(collection(db, COLLECTIONS.adminUsers)),
  ]);
  
  return {
    workspacesCount: workspacesSnapshot.data().count,
    propertiesCount: propertiesSnapshot.data().count,
    clientUsersCount: clientUsersSnapshot.data().count,
    adminUsersCount: adminUsersSnapshot.data().count,
  };
}
